import * as THREE from 'three/webgpu';
import { pass, screenUV, vec2, vec4, float, uniform, smoothstep, length } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';

import { lerp, saturate } from '../math/mathx.js';

/**
 * @file The post stage: `pass -> bloom -> vignette`.
 *
 * Two rules this file exists to enforce:
 *  1. MSAA comes from `pass(scene, camera, {samples})`, NOT from
 *     `WebGPURenderer({antialias:true})`. Once a post pipeline is in play the
 *     renderer's own MSAA does nothing, because the scene is drawn into a render
 *     target.
 *  2. Everything time-of-day-dependent is a `uniform()`, so
 *     {@link setNight} is a handful of scalar writes per frame and never
 *     recompiles a shader. `DayNight.bind({post})` calls it every fixed step —
 *     if it rebuilt the node graph the frame time would be catastrophic.
 *
 * r183 renamed `PostProcessing` to `RenderPipeline`; the `??` guard keeps a
 * three bump in either direction from bricking the app.
 *
 * @module engine/render/post
 */

/** @typedef {import('../core/Engine.js').Engine} Engine */

/**
 * @typedef {Object} PostHandle
 * @property {() => void} render Drives one frame. Assign the handle itself to
 *   `engine.post` — it satisfies {@link import('../types.js').PostStage}.
 * @property {(n: number) => void} setNight 0..1; lerps bloom + vignette between
 *   their day and night settings. Cheap (uniform writes only).
 * @property {() => void} resize Re-syncs the pipeline with the renderer size.
 *   The pipeline already tracks `renderer.getDrawingBufferSize()` on its own, so
 *   this is a safety valve, not a requirement.
 * @property {() => void} dispose Idempotent.
 * @property {any} pipeline The raw `THREE.RenderPipeline` (debug/inspection).
 * @property {any} bloom The `BloomNode` (its `.strength/.radius/.threshold` are
 *   live uniforms if you want to drive them from a debug panel).
 */

/**
 * Builds the project's post stage.
 *
 * The `bloom*` / `vignette` options are the **day-time** settings; the `*Night`
 * options are where `setNight(1)` lands. Defaults follow the plan's DayNight
 * spec (bloom 0.25->1.15 is written here as 0.35->1.15, threshold 0.80->0.45,
 * vignette 0.30->0.50 — see the handoff note).
 *
 * @param {Engine} engine
 * @param {Object} [o]
 * @param {number} [o.samples=4] MSAA samples on the scene pass. 0 disables.
 * @param {number} [o.bloomStrength=0.35] Day.
 * @param {number} [o.bloomStrengthNight=1.15]
 * @param {number} [o.bloomRadius=0.3] Constant across the cycle.
 * @param {number} [o.bloomThreshold=0.8] Day — only baked values >1 glow.
 * @param {number} [o.bloomThresholdNight=0.45] Night — lets lit windows and the
 *   moon bloom without the bake having to push absurd values.
 * @param {number} [o.vignette=0.3] Day corner darkening, 0..1.
 * @param {number} [o.vignetteNight=0.5]
 * @param {number} [o.vignetteInner=0.42] Radius (0..~1.4) where darkening starts.
 * @param {number} [o.vignetteOuter=1.15] Radius where it reaches full strength.
 * @returns {PostHandle}
 */
export function createPost(engine, o = {}) {
  const {
    samples = 4,
    bloomStrength = 0.35,
    bloomStrengthNight = 1.15,
    bloomRadius = 0.3,
    bloomThreshold = 0.8,
    bloomThresholdNight = 0.45,
    vignette = 0.3,
    vignetteNight = 0.5,
    vignetteInner = 0.42,
    vignetteOuter = 1.15,
  } = o;

  const RenderPipeline = THREE.RenderPipeline ?? THREE.PostProcessing;
  const pipeline = new RenderPipeline(engine.renderer);

  const scenePass = pass(engine.scene, engine.camera, samples > 0 ? { samples } : undefined);
  const color = scenePass.getTextureNode('output');

  const bloomPass = bloom(color, bloomStrength, bloomRadius, bloomThreshold);

  // Vignette. Hand-rolled rather than `circle()` from Shape.js because that
  // helper works in `uv()` (mesh UVs) and is aspect-blind; we want an
  // aspect-corrected radius so a 21:9 window does not get black bars of shade
  // down its sides.
  const uVignette = uniform(vignette);
  const uAspect = uniform(1);
  const centred = screenUV.sub(vec2(0.5, 0.5)).mul(vec2(uAspect, 1));
  const r = length(centred).mul(2);
  const darken = smoothstep(float(vignetteInner), float(vignetteOuter), r).mul(uVignette);

  const lit = color.add(bloomPass);
  pipeline.outputNode = vec4(lit.rgb.mul(float(1).sub(darken)), lit.a);

  const syncAspect = () => {
    const size = engine.renderer.getDrawingBufferSize(new THREE.Vector2());
    uAspect.value = size.height > 0 ? Math.max(size.width / size.height, 0.0001) : 1;
  };
  syncAspect();

  const offResize = engine.bus?.on?.('resize', syncAspect) ?? null;

  let disposed = false;

  /** @type {PostHandle} */
  const handle = {
    pipeline,
    bloom: bloomPass,

    render() {
      if (disposed) return;
      pipeline.render();
    },

    setNight(n) {
      const t = saturate(n);
      bloomPass.strength.value = lerp(bloomStrength, bloomStrengthNight, t);
      bloomPass.threshold.value = lerp(bloomThreshold, bloomThresholdNight, t);
      uVignette.value = lerp(vignette, vignetteNight, t);
    },

    resize() {
      syncAspect();
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      offResize?.();
      try {
        scenePass.dispose?.();
      } catch {
        /* noop */
      }
      try {
        bloomPass.dispose?.();
      } catch {
        /* noop */
      }
      try {
        pipeline.dispose?.();
      } catch {
        /* noop */
      }
    },
  };

  handle.setNight(0);
  return handle;
}
